import {
  ComponentProps,
  createContext,
  useCallback,
  useContext,
  useState,
} from "react";
import type { LayoutChangeEvent } from "react-native";
import * as RechartsPrimitive from "recharts";
import { isWeb, View, ViewProps } from "tamagui";

export type ChartConfig = {
  [k in string]: {
    label?: React.ReactNode;
    color?: string;
    tag?: string;
  };
};

type ChartContextProps = {
  config: ChartConfig;
  layout: {
    width: number;
    height: number;
  };
};

const ChartContext = createContext<ChartContextProps | null>(null);

export const useChartContext = () => {
  const context = useContext(ChartContext);
  if (!context) {
    throw new Error("useChartContext must be used within a ChartContainer");
  }
  return context;
};

type ChartContainerChildren = ComponentProps<
  typeof RechartsPrimitive.ResponsiveContainer
>["children"];

type ChartRenderFunction = ({
  width,
  height,
}: {
  width: number;
  height: number;
  widthScale?: number;
}) => ChartContainerChildren;

function isRenderFunction(
  child: ChartContainerChildren | ChartRenderFunction,
): child is ChartRenderFunction {
  return typeof child === "function";
}

export const ChartContainer: React.FC<
  Omit<ViewProps, "children" | "maxWidth"> & {
    config: ChartConfig;
    children: ChartContainerChildren | ChartRenderFunction;
    maxWidth?: number;
  }
> = ({ children, maxWidth, config, ...rest }) => {
  const [layout, setLayout] = useState({ width: 0, height: 0 });
  const widthScale = layout.width > 0 ? layout.width / (maxWidth ?? 0) : 1;

  const handleChangeLayout = useCallback(
    (e: LayoutChangeEvent) =>
      setLayout({
        height: e.nativeEvent.layout.height,
        width: e.nativeEvent.layout.width,
      }),
    [],
  );

  return (
    <ChartContext.Provider value={{ config, layout }}>
      <View onLayout={handleChangeLayout} maxWidth={maxWidth} {...rest}>
        <ChartStyles />
        <RechartsPrimitive.ResponsiveContainer width={"100%"} height={"100%"}>
          {isRenderFunction(children)
            ? children({ ...layout, widthScale })
            : children}
        </RechartsPrimitive.ResponsiveContainer>
      </View>
    </ChartContext.Provider>
  );
};

const ChartStyles = () => {
  if (!isWeb) return null;
  return (
    <style>
      {`
.recharts-layer:focus { 
  outline: none !important;
 }
.recharts-sector:focus {
  outline: none !important;
}
`}
    </style>
  );
};
